import moment from "moment-timezone";

/**
 * Determines the next upcoming Iqamah or Jummah event based on masjid timezone.
 * @param {moment.Moment} now - Current time (timezone-aware)
 * @param {Object} iqamahTimes - Prayer times object
 * @param {Array} jummahTimes - Jummah times array
 * @param {number} bufferMinutes - How long to keep an event "active" after it starts.
 * Use 0 for UI highlighting (strict next).
 * Use 20 for Overlays (keep showing Iqamah/Salah screens).
 * @returns {Object|null} The next event object
 */
export const getNextPrayerOrJummah = (now, iqamahTimes = {}, jummahTimes = [], bufferMinutes = 0) => {
  if (!now || !moment.isMoment(now)) {
    now = moment.tz(moment(), moment.tz.guess());
  }

  const tz = now.tz() || moment.tz.guess();
  const events = [];
  const isFriday = now.day() === 5; // 5 = Friday

  // Daily prayers (skip Dhuhr on Friday)
  const prayers = isFriday
    ? ["fajr", "asr", "maghrib", "isha"]
    : ["fajr", "dhuhr", "asr", "maghrib", "isha"];

  for (let prayer of prayers) {
    const timeStr = iqamahTimes?.[prayer];
    if (!timeStr) continue;

    let prayerTime = moment.tz(
      `${now.format("YYYY-MM-DD")} ${timeStr}`,
      ["YYYY-MM-DD h:mm A", "YYYY-MM-DD hh:mm A"],
      tz
    );

    if (!prayerTime.isValid()) continue;

    // ✅ DUAL MODE LOGIC:
    // If bufferMinutes is 0 (UI), event moves to tomorrow immediately after passing.
    // If bufferMinutes is 20 (Overlay), event stays "active" for 20 mins.
    if (now.isAfter(prayerTime.clone().add(bufferMinutes, "minutes"))) {
      prayerTime = prayerTime.add(1, "day");
    }

    events.push({
      type: "daily",
      name: prayer.toUpperCase(),
      time: prayerTime,
    });
  }

  // Jummah prayers
  if (isFriday && Array.isArray(jummahTimes)) {
    for (let entry of jummahTimes) {
      let jummahTime = moment.tz(
        `${now.format("YYYY-MM-DD")} ${entry.iqama_Time}`,
        ["YYYY-MM-DD h:mm A", "YYYY-MM-DD hh:mm A"],
        tz
      );

      if (jummahTime.isValid()) {
        // Apply the same buffer logic to Jummah
        if (now.isAfter(jummahTime.clone().add(bufferMinutes, "minutes"))) {
            continue; 
        }

        events.push({
          type: "jummah",
          name: `JUMMAH ${entry.sequence || ""}`.trim(),
          time: jummahTime,
          sequence: entry.sequence,
        });
      }
    }
  }

  if (events.length === 0) return null;

  return events.reduce((earliest, event) =>
    event.time.isBefore(earliest.time) ? event : earliest
  );
};

// ... (Keep getOverlayTypeForCurrentTime and getSecondsUntil EXACTLY as they were in my previous message) ...
// Ensure getOverlayTypeForCurrentTime still has the `isBetween` checks we fixed previously!

export const getOverlayTypeForCurrentTime = (now, nextEvent) => {
  if (!nextEvent || !nextEvent.time) return null;

  const iqamahTime = nextEvent.time.clone();
  
  const countdownStart = iqamahTime.clone().subtract(1, "minute"); 
  const iqamahEnd = iqamahTime.clone().add(1, "minute");         
  const salahEnd = iqamahTime.clone().add(11, "minute");         

  if (now.isBetween(countdownStart, iqamahTime, null, "[)")) return "countdown";
  if (now.isBetween(iqamahTime, iqamahEnd, null, "[)")) return "iqamah";
  if (now.isBetween(iqamahEnd, salahEnd, null, "[)")) return "salah";

  return null;
};

export const getSecondsUntil = (now, targetTime) => {
  return Math.max(0, Math.floor(targetTime.diff(now, "seconds")));
};